import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const ACCENT = '#2B7FFF';
const EMPTY = { name: '', description: '', duration_minutes: 30, price: '', after_hours_price: '', is_mobile: false, request_only: false, active: true };

export default function Services() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/services/all').then(r => setServices(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name,
      description: s.description || '',
      duration_minutes: s.duration_minutes,
      price: s.price,
      after_hours_price: s.after_hours_price || '',
      is_mobile: s.is_mobile || false,
      request_only: s.request_only || false,
      active: s.active,
    });
  };
  const handleCancel = () => { setEditing(null); setForm(EMPTY); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        after_hours_price: form.after_hours_price !== '' ? form.after_hours_price : null,
      };
      if (editing) {
        await api.put(`/services/${editing}`, payload);
      } else {
        await api.post('/services', payload);
      }
      handleCancel();
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    await api.delete(`/services/${id}`);
    load();
  };

  return (
    <AdminLayout>
      <h1 className="page-title" style={{ marginBottom: '8px' }}>Services</h1>
      <p className="text-muted text-sm" style={{ marginBottom: '32px' }}>Manage service offerings and pricing</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 380px', gap: '24px', alignItems: 'start' }}>
        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading ? <div className="spinner" /> : services.map(s => (
            <div key={s.id} className="card" style={{ opacity: s.active ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '16px' }}>{s.name}</span>
                    {!s.active && <span style={{ color: '#6b7280', fontSize: '12px' }}>(inactive)</span>}
                    {s.is_mobile && <span style={{ background: 'rgba(43,127,255,0.12)', color: ACCENT, fontSize: '11px', padding: '2px 7px', borderRadius: '20px', border: '1px solid rgba(43,127,255,0.3)' }}>Mobile</span>}
                    {s.request_only && <span style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa', fontSize: '11px', padding: '2px 7px', borderRadius: '20px', border: '1px solid rgba(59,130,246,0.3)' }}>Request Only</span>}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '13px', marginTop: '3px' }}>{s.description}</div>
                  <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '5px' }}>⏱ {s.duration_minutes} min</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ color: ACCENT, fontWeight: 700, fontSize: '20px' }}>${parseFloat(s.price).toFixed(0)}</div>
                  {s.after_hours_price && (
                    <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px' }}>
                      After hours: <strong style={{ color: ACCENT }}>${parseFloat(s.after_hours_price).toFixed(0)}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px', justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost" style={{ padding: '5px 10px', fontSize: '12px' }} onClick={() => handleEdit(s)}>Edit</button>
                    {s.active && (
                      <button className="btn btn-danger" style={{ padding: '5px 10px', fontSize: '12px' }} onClick={() => handleDeactivate(s.id)}>Deactivate</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="card" style={{ position: 'sticky', top: '80px' }}>
          <h3 style={{ fontWeight: 700, fontSize: '16px', marginBottom: '20px' }}>{editing ? 'Edit Service' : 'Add New Service'}</h3>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label className="label">Name</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea className="input" value={form.description} onChange={e => set('description', e.target.value)} rows={2} style={{ resize: 'none' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label className="label">Duration (min)</label>
                <input className="input" type="number" min="5" step="5" value={form.duration_minutes} onChange={e => set('duration_minutes', parseInt(e.target.value))} required />
              </div>
              <div>
                <label className="label">Regular Price ($)</label>
                <input className="input" type="number" min="0" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} required />
              </div>
            </div>
            <div>
              <label className="label">After-Hours Price ($) <span style={{ color: '#6b7280', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— leave blank if none</span></label>
              <input className="input" type="number" min="0" step="0.01" placeholder="e.g. 75" value={form.after_hours_price} onChange={e => set('after_hours_price', e.target.value)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#9ca3af' }}>
                <input type="checkbox" checked={form.is_mobile} onChange={e => set('is_mobile', e.target.checked)} />
                Mobile service (comes to client)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#9ca3af' }}>
                <input type="checkbox" checked={form.request_only} onChange={e => set('request_only', e.target.checked)} />
                Request-only (no time slot picker)
              </label>
              {editing && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#9ca3af' }}>
                  <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
                  Active
                </label>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving...' : editing ? 'Update Service' : 'Add Service'}
              </button>
              {editing && <button type="button" className="btn btn-ghost" onClick={handleCancel}>Cancel</button>}
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
